// ink-component
import React, { ReactNode, MouseEvent } from 'react';
import { ThemeProvider } from 'styled-components';
import useWindowWidth from '../../hooks/useWindowWidth';
import useMutationObserver from '../../hooks/useMutationObserver';
import BlockFooter from './BlockFooter/BlockFooter';
import BlockHeader from './BlockHeader/BlockHeader';
import { IconTypes } from '../../helpers/constants';
import getGlobalProps from '../../helpers/constants/global-props';
import { BlockBodyWrapper, BlockWrapper } from './styles';

export type PrimaryActionType = {
  label?: ReactNode;
  callback?: (event?: any) => void;
  type?: 'danger' | 'primary';
  size?: 'wide' | 'full';
  'data-testid'?: string | number;
};

export type SecondaryActionType = {
  label?: ReactNode;
  callback?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  'data-testid'?: string | number;
};

export type TertiaryActionType = {
  label?: ReactNode;
  callback?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  'data-testid'?: string | number;
};

/*
  Notes:

  1. We should be able to deprecate single-action props via codemod since we can
  now pass them via `PrimaryAction`
    - `actionCallback`
    - `actionSize`
    - `actionText`
    - `actionType`
*/

export interface BlockProps {
  /** Title */
  title?: string;
  /** Content */
  children?: ReactNode;
  /** Transparency modifier */
  isTransparent?: boolean;
  /** Disables all footer buttons and the buttons associated with `iconLeftType` and `iconRightType` while async processes are running. If you're creating a `Modal`, consider also using `Modal`'s `onRequestClose` prop for consistent disabled behavior. */
  actionIsLoading?: boolean;
  /** Type of the icon on the left of the header */
  iconLeftType?: IconTypes;
  /** Passed into the data-testid of the left icon in the Block header */
  iconLeftTestId?: string;
  /** Callback for the left icon */
  iconLeftCallback?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  /** Type of the icon on the right of the header */
  iconRightType?: IconTypes;
  /** Passed into the data-testid of the right icon in the Block header */
  iconRightTestId?: string;
  /** Callback for the right icon */
  iconRightCallback?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  /** Primary action configuration object. Pass it as the only action for single-action behavior. */
  primaryAction?: PrimaryActionType;
  /** Secondary action configuration object. */
  secondaryAction?: SecondaryActionType;
  /** Tertiary action configuration object. */
  tertiaryAction?: TertiaryActionType;
  /** Removes built-in spacing */
  trim?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;

  /** @ignore */
  isModal?: boolean;
  /** @ignore */
  isLoading?: boolean;
  /** @ignore */
  actionText?: ReactNode;
  /** @ignore */
  actionType?: 'danger' | 'primary';
  /** @ignore */
  actionCallback?: (nextStep?: () => void) => Record<string, unknown>;
  /** @ignore */
  actionSize?: 'wide' | 'full' | undefined;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Wraps content in a card layout, with optional header and footer
 * @categories layout
 * @aka box, container
 */
function Block(props: BlockProps) {
  const {
    children,
    title,
    isTransparent,
    isModal,
    actionText,
    primaryAction,
    isLoading,
    actionIsLoading,
    trim,
  } = props;
  const { 'data-testid': dataTestId, ...otherProps } = props;

  const { isMobile } = useWindowWidth();

  const [formId, setFormId] = React.useState<string | undefined>(undefined);

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const blockRef = React.useRef<HTMLDivElement>(null);

  const setChildFormId = React.useCallback(
    (element: HTMLElement) => {
      const childFormId = element.querySelector('form')?.id;
      setFormId(childFormId);
    },
    [setFormId],
  );

  useMutationObserver({
    ref: blockRef,
    callback: setChildFormId,
    observerOptions: { subtree: true, childList: true },
  });

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        modal: isModal,
        mobile: isMobile,
        transparent: isTransparent,
        trim,
      })}
    >
      <BlockWrapper {...globalProps} data-testid={dataTestId}>
        {title && <BlockHeader {...otherProps} />}
        <BlockBodyWrapper ref={blockRef}>{children}</BlockBodyWrapper>
        {(actionText || primaryAction?.label) && (
          <BlockFooter {...otherProps} formId={formId} actionIsLoading={isLoading || actionIsLoading} />
        )}
      </BlockWrapper>
    </ThemeProvider>
  );
}

Block.defaultProps = {
  title: undefined,
  children: undefined,
  isTransparent: false,
  actionIsLoading: false,
  iconLeftType: undefined,
  iconLeftCallback: () => {},
  iconRightType: undefined,
  iconRightCallback: () => {},
  primaryAction: {
    label: undefined,
    type: 'primary',
    size: undefined,
    callback: () => {},
    'data-testid': undefined,
  },
  secondaryAction: {
    label: undefined,
    callback: () => {},
    'data-testid': undefined,
  },
  tertiaryAction: {
    label: undefined,
    callback: () => {},
    'data-testid': undefined,
  },
  trim: false,
  'data-testid': undefined,

  isModal: false,
  actionSize: 'wide',
  isLoading: false,
  actionText: undefined,
  actionType: undefined,
  actionCallback: undefined,
};

Block.displayName = 'Block';

export default Block;
